import React, { useState } from 'react';
import { Sym, IconButton, Switch } from '../../../ui';
import type { DeviceModelOutput } from '@grosync/common';
import {
  sensorMeta,
  sensorDefaults,
  SENSOR_TYPE_OPTIONS,
  DEVICE_TYPE_OPTIONS,
  deviceTypeLabel,
  GROWTH_FIELD_CATALOG,
  normalizeGrowthFields,
} from '../catalog';

type CSS = React.CSSProperties;

// Types previously imported from the store — defined locally so this component
// has no dependency on the Zustand deviceModelStore.
export type SensorDataType = 'number' | 'boolean' | 'string';

interface SensorSpec {
  sensorType: string;
  displayName: string;
  unit: string;
  dataType: SensorDataType;
  minValue?: number;
  maxValue?: number;
}

interface ActuatorSpec {
  channel: string;
  displayName: string;
  type: 'relay' | 'pwm';
}

/** Payload shape the modal emits via onSave — matches CreateDeviceModelInput at runtime */
export type ModelDraft = {
  name: string;
  code: string;
  deviceType: string;
  description?: string;
  tagline?: string;
  sensors: SensorSpec[];
  actuators?: ActuatorSpec[];
  growthEnabled: boolean;
  growthFields: string[];
  comm?: string;
  battery?: string;
  interval?: string;
  firmware?: string;
};

const inputStyle: CSS = {
  height: 44,
  padding: '0 14px',
  borderRadius: 10,
  width: '100%',
  border: '1px solid var(--md-sys-color-outline-variant)',
  background: 'var(--md-sys-color-surface-container-low)',
  font: '400 14px/20px var(--gs-font-kr)',
  color: 'var(--md-sys-color-on-surface)',
  outline: 'none',
  boxSizing: 'border-box',
};
const selectStyle: CSS = { ...inputStyle, cursor: 'pointer' };
const smallInput: CSS = { ...inputStyle, height: 38, padding: '0 10px' };

/** 센서 스펙 한 줄 그리드 — 아이콘·타입·파라미터·표시명·단위·데이터타입·min·max·삭제 */
const SENSOR_GRID =
  '26px minmax(120px,1.3fr) minmax(96px,1fr) minmax(110px,1.2fr) minmax(54px,0.7fr) minmax(92px,0.9fr) minmax(56px,0.6fr) minmax(56px,0.6fr) 32px';

const ColHead = ({ children }: { children: React.ReactNode }): React.ReactElement => (
  <span style={{
    font: '600 10px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  }}>{children}</span>
);

const Field = ({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): React.ReactElement => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
    <span style={{ font: '600 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
      {label}
      {required && <span style={{ color: 'var(--gs-error)' }}> *</span>}
    </span>
    {children}
  </label>
);

interface SensorRow {
  sensorType: string;
  displayName: string;
  unit: string;
  dataType: SensorDataType;
  minValue: string;
  maxValue: string;
}

function specToRow(s: SensorSpec): SensorRow {
  return {
    sensorType: s.sensorType,
    displayName: s.displayName,
    unit: s.unit,
    dataType: s.dataType,
    minValue: s.minValue != null ? String(s.minValue) : '',
    maxValue: s.maxValue != null ? String(s.maxValue) : '',
  };
}

function toNum(v: string): number | undefined {
  if (v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

function rowToSpec(r: SensorRow): SensorSpec {
  const min = r.dataType === 'number' ? toNum(r.minValue) : undefined;
  const max = r.dataType === 'number' ? toNum(r.maxValue) : undefined;
  return {
    sensorType: r.sensorType,
    displayName: r.displayName || sensorMeta(r.sensorType).label,
    unit: r.unit,
    dataType: r.dataType,
    ...(min !== undefined ? { minValue: min } : {}),
    ...(max !== undefined ? { maxValue: max } : {}),
  };
}

function newRow(sensorType: string): SensorRow {
  const d = sensorDefaults(sensorType);
  return {
    sensorType,
    displayName: sensorMeta(sensorType).label,
    unit: d.unit,
    dataType: 'number',
    minValue: d.min != null ? String(d.min) : '',
    maxValue: d.max != null ? String(d.max) : '',
  };
}

/** 직접 입력(수동) 센서 행 — 영문 파라미터·표시명·단위를 자유 입력 */
function customRow(): SensorRow {
  return { sensorType: '', displayName: '', unit: '', dataType: 'number', minValue: '', maxValue: '' };
}

/** 타입 셀렉트의 "직접 입력" 옵션 sentinel */
const CUSTOM_TYPE = '__custom__';

/** ESP32가 발행하는 sensorType 파라미터 형식: 소문자 영문 시작 + 영숫자·밑줄 */
const PARAM_RE = /^[a-z][a-z0-9_]*$/;

/** 영문 파라미터 입력 정규화 — 소문자 + 영숫자/밑줄만 허용 */
function normalizeParam(v: string): string {
  return v.toLowerCase().replace(/[^a-z0-9_]/g, '');
}

export const ModelFormModal = ({
  initial,
  defaultName,
  onClose,
  onSave,
}: {
  initial?: DeviceModelOutput | null;
  defaultName: string;
  onClose: () => void;
  onSave: (draft: ModelDraft) => void;
}): React.ReactElement => {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? defaultName);
  const [code, setCode] = useState(initial?.code ?? '');
  const [deviceType, setDeviceType] = useState(initial?.deviceType ?? DEVICE_TYPE_OPTIONS[0]);
  const [tagline, setTagline] = useState(initial?.tagline ?? '');
  const [desc, setDesc] = useState(initial?.description ?? '');
  const [comm, setComm] = useState(initial?.comm ?? 'LTE-M / Wi-Fi');
  const [battery, setBattery] = useState(initial?.battery ?? '6,000mAh · 약 30일');
  const [interval, setIntervalValue] = useState(initial?.interval ?? '1분');
  const [firmware, setFirmware] = useState(initial?.firmware ?? 'v1.0.0');
  const [growth, setGrowth] = useState(initial?.growthEnabled ?? false);
  const [growthFields, setGrowthFields] = useState<string[]>(
    initial ? normalizeGrowthFields(initial.growthFields) : ['plantHeight', 'healthScore', 'photo'],
  );
  const [rows, setRows] = useState<SensorRow[]>(
    initial?.sensors?.length
      ? initial.sensors.map((s) => specToRow(s as SensorSpec))
      : [newRow(SENSOR_TYPE_OPTIONS[0])],
  );

  const usedTypes = rows.map((r) => r.sensorType);
  const addRow = (): void => {
    setRows((prev) => {
      const used = prev.map((r) => r.sensorType);
      const avail = SENSOR_TYPE_OPTIONS.find((t) => !used.includes(t));
      // 사용 가능한 빌트인 타입이 없으면 직접 입력 행을 추가한다
      return [...prev, avail ? newRow(avail) : customRow()];
    });
  };
  const setRow = (i: number, patch: Partial<SensorRow>): void =>
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const changeType = (i: number, t: string): void =>
    setRows(rows.map((r, idx) => (idx === i ? (t === CUSTOM_TYPE ? customRow() : newRow(t)) : r)));
  const removeRow = (i: number): void => setRows(rows.filter((_, idx) => idx !== i));

  // 액추에이터(제어 채널) 행
  const [actuators, setActuators] = useState<ActuatorSpec[]>(initial?.actuators as ActuatorSpec[] ?? []);
  const setActuator = (i: number, patch: Partial<ActuatorSpec>): void =>
    setActuators((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  const addActuator = (): void =>
    setActuators((prev) => [...prev, { channel: '', displayName: '', type: 'relay' }]);
  const removeActuator = (i: number): void =>
    setActuators((prev) => prev.filter((_, idx) => idx !== i));

  const toggleGrowth = (v: string): void =>
    setGrowthFields((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const codeValid = /^[A-Z0-9]{2,6}$/.test(code.trim());
  // 모든 행의 영문 파라미터가 형식에 맞고, 중복이 없어야 저장 가능
  const paramsValid = rows.every((r) => PARAM_RE.test(r.sensorType.trim()));
  const dupParam = (() => {
    const seen = new Set<string>();
    for (const r of rows) {
      const k = r.sensorType.trim();
      if (!k) continue;
      if (seen.has(k)) return true;
      seen.add(k);
    }
    return false;
  })();
  const canSave =
    name.trim().length > 0 &&
    codeValid &&
    rows.length > 0 &&
    paramsValid &&
    !dupParam &&
    (!growth || growthFields.length > 0);

  const save = (): void => {
    if (!canSave) return;
    onSave({
      name: name.trim(),
      code: code.trim().toUpperCase(),
      deviceType,
      description: desc.trim() || undefined,
      tagline: tagline.trim() || undefined,
      sensors: rows.map(rowToSpec),
      actuators: actuators
        .map((a) => ({ ...a, channel: a.channel.trim(), displayName: a.displayName.trim() }))
        .filter((a) => a.channel.length > 0),
      growthEnabled: growth,
      growthFields: growth ? growthFields : [],
      comm,
      battery,
      interval,
      firmware: firmware.trim() || 'v1.0.0',
    });
    onClose();
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 30 }} />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          zIndex: 31,
          width: 'min(920px, 94vw)',
          maxHeight: '88%',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--md-sys-color-surface)',
          borderRadius: 24,
          overflow: 'hidden',
          boxShadow: '0 24px 60px -12px rgba(0,0,0,0.45)',
          animation: 'modalIn 220ms cubic-bezier(.2,0,0,1)',
        }}
      >
        <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--md-sys-color-outline-variant)' }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--md-sys-color-primary-container)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sym name={isEdit ? 'edit' : 'add_box'} size={22} fill={1} style={{ color: 'var(--md-sys-color-on-primary-container)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ font: '700 18px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
              {isEdit ? '장비모델 수정' : '장비모델 추가'}
            </div>
            <div style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginTop: 2 }}>
              센서 스펙·생육 항목·제품 사양을 구성합니다
            </div>
          </div>
          <IconButton icon="close" size={22} onClick={onClose} />
        </div>

        <div className="gs-scroll-hide" style={{ flex: 1, overflowY: 'auto', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="모델명" required>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 모델 3" style={inputStyle} />
            </Field>
            <Field label="모델 코드" required>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="예: GS1"
                maxLength={6}
                style={inputStyle}
              />
              {code.length > 0 && !codeValid && (
                <span style={{ font: '400 11px/1.4 var(--gs-font-kr)', color: 'var(--gs-error)' }}>
                  대문자 영숫자 2~6자
                </span>
              )}
            </Field>
            <Field label="장비 유형" required>
              <select value={deviceType} onChange={(e) => setDeviceType(e.target.value)} style={selectStyle}>
                {DEVICE_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{deviceTypeLabel(t)}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="유형 (선택)">
            <input value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="예: 수경재배 특화형" style={inputStyle} />
          </Field>
          <Field label="설명 (선택)">
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="모델 용도와 특징" rows={2}
              style={{ ...inputStyle, height: 'auto', resize: 'vertical', padding: '10px 14px', lineHeight: '20px' }} />
          </Field>

          <div>
            <div style={{ font: '600 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginBottom: 10 }}>
              센서 스펙 <span style={{ color: 'var(--md-sys-color-on-surface-variant)', fontWeight: 400 }}>({rows.length}개)</span>
            </div>
            <div className="gs-scroll-hide" style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 700 }}>
                {/* 컬럼 헤더 */}
                <div style={{ display: 'grid', gridTemplateColumns: SENSOR_GRID, gap: 8, alignItems: 'center', padding: '0 2px 6px' }}>
                  <span />
                  <ColHead>센서 타입</ColHead>
                  <ColHead>파라미터 (ESP32)</ColHead>
                  <ColHead>표시명</ColHead>
                  <ColHead>단위</ColHead>
                  <ColHead>데이터 타입</ColHead>
                  <ColHead>min</ColHead>
                  <ColHead>max</ColHead>
                  <span />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rows.map((r, i) => {
                    const meta = sensorMeta(r.sensorType);
                    const known = SENSOR_TYPE_OPTIONS.includes(r.sensorType);
                    const isCustom = !known; // 빌트인 타입이 아니면 직접 입력 모드
                    const opts = SENSOR_TYPE_OPTIONS.filter((t) => t === r.sensorType || !usedTypes.includes(t));
                    const selectVal = known ? r.sensorType : CUSTOM_TYPE;
                    const paramOk = PARAM_RE.test(r.sensorType.trim());
                    const showParamErr = isCustom && r.sensorType.length > 0 && !paramOk;
                    const isNum = r.dataType === 'number';
                    return (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: SENSOR_GRID, gap: 8, alignItems: 'center' }}>
                          <div style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0, background: 'var(--md-sys-color-surface-container-high)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Sym name={meta.icon} size={16} fill={1} style={{ color: meta.tone }} />
                          </div>
                          <select value={selectVal} onChange={(e) => changeType(i, e.target.value)} style={{ ...smallInput, cursor: 'pointer' }}>
                            {opts.map((t) => (
                              <option key={t} value={t}>{sensorMeta(t).label}</option>
                            ))}
                            <option value={CUSTOM_TYPE}>직접 입력</option>
                          </select>
                          {isCustom ? (
                            <input
                              value={r.sensorType}
                              onChange={(e) => setRow(i, { sensorType: normalizeParam(e.target.value) })}
                              placeholder="예: soil_m"
                              spellCheck={false}
                              title={showParamErr
                                ? '소문자 영문으로 시작, 영숫자·밑줄만 사용 (예: leaf_temp)'
                                : 'ESP32 펌웨어의 sensorType와 정확히 일치시켜 주세요'}
                              style={{ ...smallInput, borderColor: showParamErr ? 'var(--gs-error)' : undefined }}
                            />
                          ) : (
                            <div title="ESP32 표준 파라미터" style={{ ...smallInput, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, cursor: 'default' }}>
                              <span style={{ color: 'var(--md-sys-color-on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.sensorType}</span>
                              <Sym name="check_circle" size={13} fill={1} style={{ color: 'var(--md-sys-color-primary)', flexShrink: 0 }} />
                            </div>
                          )}
                          <input value={r.displayName} onChange={(e) => setRow(i, { displayName: e.target.value })} placeholder="표시명(한글)" style={smallInput} />
                          <input value={r.unit} onChange={(e) => setRow(i, { unit: e.target.value })} placeholder="단위" style={smallInput} />
                          <select value={r.dataType} onChange={(e) => setRow(i, { dataType: e.target.value as SensorDataType })} style={{ ...smallInput, cursor: 'pointer' }}>
                            <option value="number">number</option>
                            <option value="boolean">boolean</option>
                            <option value="string">string</option>
                          </select>
                          <input type="number" value={r.minValue} disabled={!isNum} onChange={(e) => setRow(i, { minValue: e.target.value })} placeholder="min" style={{ ...smallInput, opacity: isNum ? 1 : 0.4 }} />
                          <input type="number" value={r.maxValue} disabled={!isNum} onChange={(e) => setRow(i, { maxValue: e.target.value })} placeholder="max" style={{ ...smallInput, opacity: isNum ? 1 : 0.4 }} />
                          <button type="button" onClick={() => removeRow(i)} disabled={rows.length <= 1}
                            style={{ width: 32, height: 32, borderRadius: 9999, border: 0, background: 'transparent', cursor: rows.length <= 1 ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Sym name="delete" size={17} style={{ color: rows.length <= 1 ? 'var(--md-sys-color-outline-variant)' : 'var(--gs-error)' }} />
                          </button>
                        </div>
                        {showParamErr && (
                          <span style={{ font: '400 10px/1.4 var(--gs-font-kr)', color: 'var(--gs-error)', paddingLeft: 34 }}>
                            소문자 영문으로 시작, 영숫자·밑줄만 사용 (예: leaf_temp)
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <button type="button" onClick={addRow}
              style={{ marginTop: 10, width: '100%', height: 44, borderRadius: 12, cursor: 'pointer', border: '1px dashed var(--md-sys-color-outline)', background: 'transparent', color: 'var(--md-sys-color-primary)', font: '600 14px/1 var(--gs-font-kr)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Sym name="add" size={18} />센서 추가
            </button>
          </div>

          {/* 액추에이터(제어 채널) */}
          <div>
            <div style={{ font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginBottom: 8 }}>
              액추에이터 (제어 채널) <span style={{ color: 'var(--md-sys-color-on-surface-variant)', fontWeight: 400 }}>({actuators.length}개)</span>
            </div>
            {actuators.length === 0 ? (
              <div style={{ padding: '10px 0 14px', font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                릴레이·PWM 등 제어 채널을 추가하면 장비 상세에서 제어 버튼이 표시됩니다. 채널값은 ESP32 펌웨어와 정확히 일치시켜 주세요.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {actuators.map((a, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 0.9fr 40px', gap: 8, alignItems: 'center' }}>
                    <input value={a.channel} placeholder="채널 (예: r1)"
                      spellCheck={false}
                      title="ESP32 펌웨어의 채널 식별자와 정확히 일치"
                      onChange={(e) => setActuator(i, { channel: e.target.value })}
                      style={smallInput} />
                    <input value={a.displayName} placeholder="표시명 (예: 급수 펌프)"
                      onChange={(e) => setActuator(i, { displayName: e.target.value })}
                      style={smallInput} />
                    <select value={a.type}
                      onChange={(e) => setActuator(i, { type: e.target.value as ActuatorSpec['type'] })}
                      style={{ ...smallInput, cursor: 'pointer' }}>
                      <option value="relay">릴레이</option>
                      <option value="pwm">PWM</option>
                    </select>
                    <button type="button" onClick={() => removeActuator(i)}
                      style={{ width: 32, height: 32, borderRadius: 9999, border: 0, background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Sym name="delete" size={17} style={{ color: 'var(--gs-error)' }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button type="button" onClick={addActuator}
              style={{ marginTop: 10, width: '100%', height: 44, borderRadius: 12, cursor: 'pointer', border: '1px dashed var(--md-sys-color-outline)', background: 'transparent', color: 'var(--md-sys-color-primary)', font: '600 14px/1 var(--gs-font-kr)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Sym name="add" size={18} />채널 추가
            </button>
          </div>

          <div onClick={() => setGrowth(!growth)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 14, cursor: 'pointer', background: growth ? 'var(--md-sys-color-primary-container)' : 'var(--md-sys-color-surface-container-low)', border: `1px solid ${growth ? 'transparent' : 'var(--md-sys-color-outline-variant)'}` }}>
            <Sym name="eco" size={22} fill={1} style={{ color: growth ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface-variant)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ font: '600 14px/18px var(--gs-font-kr)', color: growth ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface)' }}>생육 데이터 수집</div>
              <div style={{ font: '400 12px/16px var(--gs-font-kr)', color: growth ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface-variant)', opacity: 0.85, marginTop: 2 }}>매일 12:00 자동 촬영 + 영상 분석</div>
            </div>
            <Switch checked={growth} onChange={setGrowth} />
          </div>
          {growth && (
            <div>
              <div style={{ font: '600 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginBottom: 10 }}>
                수집 항목 선택 {growthFields.length === 0 && <span style={{ color: 'var(--gs-error)', fontWeight: 400 }}>· 1개 이상 필요</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
                {GROWTH_FIELD_CATALOG.map((g) => {
                  const on = growthFields.includes(g.value);
                  return (
                    <label key={g.value} onClick={(e) => { e.preventDefault(); toggleGrowth(g.value); }}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 11px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${on ? 'transparent' : 'var(--md-sys-color-outline-variant)'}`, background: on ? 'var(--md-sys-color-primary-container)' : 'var(--md-sys-color-surface-container-low)' }}>
                      <Sym name={g.icon} size={18} fill={on ? 1 : 0} style={{ color: on ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface-variant)', marginTop: 1 }} />
                      <div>
                        <div style={{ font: '600 13px/17px var(--gs-font-kr)', color: on ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface)' }}>{g.label}</div>
                        <div style={{ font: '400 10px/14px var(--gs-font-kr)', color: on ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface-variant)', opacity: 0.8, marginTop: 1 }}>{g.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="통신 방식">
              <select value={comm} onChange={(e) => setComm(e.target.value)} style={selectStyle}>
                {['LTE-M / Wi-Fi', 'Wi-Fi', 'LTE-M', 'LoRa', '유선 (RS-485)'].map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </Field>
            <Field label="전송 주기">
              <input value={interval} onChange={(e) => setIntervalValue(e.target.value)} placeholder="예: 1분" style={inputStyle} />
            </Field>
            <Field label="펌웨어 버전">
              <input value={firmware} onChange={(e) => setFirmware(e.target.value)} placeholder="예: v1.0.0" style={inputStyle} />
            </Field>
            <Field label="배터리">
              <input value={battery} onChange={(e) => setBattery(e.target.value)} placeholder="예: 6,000mAh · 약 30일" style={inputStyle} />
            </Field>
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--md-sys-color-outline-variant)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose}
            style={{ height: 44, padding: '0 20px', borderRadius: 9999, cursor: 'pointer', background: 'transparent', border: '1px solid var(--md-sys-color-outline)', color: 'var(--md-sys-color-on-surface)', font: '600 14px/1 var(--gs-font-kr)' }}>
            취소
          </button>
          <button type="button" onClick={save} disabled={!canSave}
            style={{ height: 44, padding: '0 22px', borderRadius: 9999, border: 0, cursor: canSave ? 'pointer' : 'default', background: canSave ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-surface-container-high)', color: canSave ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-outline)', font: '600 14px/1 var(--gs-font-kr)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Sym name="check" size={18} />{isEdit ? '변경 저장' : '모델 등록'}
          </button>
        </div>
      </div>
    </>
  );
};
